import { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { IChart } from "../../../../../@types/chart.type";
import iconDelete from "./../../../../../assets/_TrashIcon_gray.svg";
import { storageUserGet } from "../../../../../storage/storageUser";
import { getProjectVision } from "../../../../../services/api/widgets/widgetsService";
import { IApiResponse } from "../../../../../@types/api-response.type";
import { IProjectVision } from "../../../../../@types/widgets.type";
import { IconProject } from "../../../../../icons/IconProject";
import { formatCurrency } from "../../../../../utils/formatCurrency";
import styles from "./styles.module.scss";

interface ProjectVisionProps {
  chart: IChart;
  ismodeEdit: boolean;
  deleteChart: (id: string | undefined, widgetId: string) => void;
  rowHeightGrid: number;
}

export function ProjectVision({
  chart,
  deleteChart,
  ismodeEdit,
  rowHeightGrid,
}: ProjectVisionProps) {
  const [data, setData] = useState<IProjectVision[]>([]);
  const [projectsName, setProjectsName] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const user = storageUserGet();

  useEffect(() => {
    async function fetchProjectVision() {
      try {
        setLoading(true);
        const response = (await getProjectVision(
          user.email,
          user?.resourceId as string
        )) as IApiResponse<IProjectVision[]>;

        if (response.statusCode === 200) {
          setData(response.result);
          setProjectsName(response.result.map((value) => value.projectName));
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
    fetchProjectVision();
  }, [user.email, user?.resourceId]);

  const cost = useMemo(() => {
    return data?.map((value) => value?.cost || 0);
  }, [data]);

  const plannedCost = useMemo(() => {
    return data?.map((value) => value?.plannedCost || 0);
  }, [data]);

  const allocatedCost = useMemo(() => {
    return data?.map((value) => value?.allocatedCost || 0);
  }, [data]);

  const series = [
    {
      name: "Custo Real",
      data: cost,
    },
    {
      name: "Custo Planejado",
      data: plannedCost,
    },
    {
      name: "Custo Alocado",
      data: allocatedCost,
    },
  ];

  const options = {
    chart: {
      type: "bar",
      stacked: false,
      zoom: {
        enabled: false,
      },
      toolbar: { show: false },
    },
    xaxis: {
      categories: projectsName,
      labels: {
        formatter: (value: string) => {
          return value.length > 10 ? value.slice(0, 10) + "..." : value;
        },
        style: {
          fontSize: "12px",
          fontFamily: "Archivo, sans-serif",
          fontWeight: 500,
          colors: ["#616161"],
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        borderRadius: 4,
        borderRadiusApplication: "end",
      },
    },
    dataLabels: {
      enabled: false,
    },
    yaxis: {
      labels: {
        formatter: (val: number) => `${formatCurrency(val)}`,
        style: {
          fontSize: "12px",
          fontFamily: "Archivo, sans-serif",
          fontWeight: 500,
          colors: ["#616161"],
        },
      },
    },
    tooltip: {
      custom: function ({ series, dataPointIndex, w }) {
        const projectName = w.globals.labels[dataPointIndex];

        const allSeries = series
          .map((s, i) => {
            const seriesName = w.config.series[i].name;
            const value = s[dataPointIndex];
            const formattedValue = new Intl.NumberFormat("pt-BR", {
              style: "currency",
              currency: "BRL",
              minimumFractionDigits: 2,
            }).format(value ?? 0);

            return `<span>${seriesName}: ${formattedValue}</span>`;
          })
          .join("");

        return `
          <div class='labelCustom' >
            <span style="margin-bottom: 6px;"><b>${projectName}</b></span>
            ${allSeries}
          </div>
        `;
      },
    },
  };

  return (
    <div className={styles.widget}>
      <div className={styles.header}>
        <h3>
          <IconProject />
          Meus Projetos (Planejado x Realizado)
        </h3>
      </div>
      <div style={{ width: "100%", height: "100%" }}>
        {loading || data?.length === 0 ? (
          <p style={{ textAlign: "center", color: "#212121" }}>Carregando...</p>
        ) : (
          <>
            {data?.length > 0 ? (
              <ReactApexChart
                options={options}
                series={series}
                type="bar"
                width="100%"
                height={
                  chart.layout.h == 1 ? rowHeightGrid - 46 : rowHeightGrid + 150
                }
              />
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "2rem",
                  color: "#616161",
                }}
              >
                <p style={{ marginTop: "1rem" }}>Nenhum dado encontrado.</p>
              </div>
            )}
          </>
        )}
      </div>
      {ismodeEdit && (
        <button onClick={() => deleteChart(chart.id, chart.widgetId)}>
          <img src={iconDelete} alt="" />
        </button>
      )}
    </div>
  );
}
